// クラブ専用の共有 Web Audio グラフ。
// 音楽 (= WHEP 配信) と声 (= ピア音声) を別バスに分離するのが要点:
//   musicBus  : speaker からの WHEP 音。zoneFilter (BAR でこもる) + zoneGain を通り、
//               post-fader で wetSend (reverb) を分岐 → musicCrowdLowShelf → musicLimiter → destination
//   voiceBus  : ピア音声。zoneFilter / zoneGain / wet には流さない。
//               voiceCrowdLowShelf → voiceLimiter → destination
//
// これにより BAR ゾーンでは「BGM だけこもって声はクリア」が実現する。
// wet は post-fader 構成にしてあるので、dry が下がると wet も比例して下がる
// (BAR の reverbWet=0.04 が「ほぼ wet なし」として正しく聞こえる)。
//
// **2 段リミッター + bus 別 crowdLowShelf 構成**:
//   旧設計 (limiter 1 段) では music + voice の合計が単一リミッターを叩くので、
//   音楽の大音量で voice 側まで一緒にダッキングされて声が不自然に小さくなる現象が
//   あった。音楽 / 声をそれぞれ独立した limiter で踏んでから合流させることで、
//   片方の peak 時にもう片方のラウドネスが揺れないようにする。
//   crowdLowShelf も bus ごとに分けて limiter の **前段** に置き、「destination 直前は
//   limiter」設計意図を保つ。limiter threshold は -6dBFS にして 2 bus 合算で 0dBFS を
//   超えない (両方が同時に limit を踏んでも -6+6=0)。

import type { Ambience } from "./map";

// 2 つの limiter 出力が destination で合算するため、threshold は -6dBFS。
// 両方が同時に -6dBFS で踏んでも合算 0dBFS で digital clip しない安全マージン。
const LIMITER_THRESHOLD_DB = -6;
const LIMITER_KNEE = 0;
const LIMITER_RATIO = 20;
const LIMITER_ATTACK = 0.003;
const LIMITER_RELEASE = 0.1;

export interface ClubGraph {
  /** 音楽 (WHEP speakers) を集める入口。zoneFilter を通って destination へ */
  musicBus: GainNode;
  /** ピア音声を集める入口。zoneFilter を通らない (= ゾーン変化に左右されない) */
  voiceBus: GainNode;
  /** ConvolverNode への送り。zoneGain の **後ろ** から分岐 (= post-fader send) */
  wetSend: GainNode;
  /** Convolver の出力を musicCrowdLowShelf に渡す gain。IR ロードに失敗したら 0 のまま */
  masterWet: GainNode;
  /** ゾーンの「こもり」(lowpass) — music 専用 */
  zoneFilter: BiquadFilterNode;
  /** ゾーンごとの音量変化 (バー 0.35 / ブース 1.4 など) — music 専用 */
  zoneGain: GainNode;
  /** 人数比例の低音減衰 — music bus 側。setCrowdCount で voice 側と一緒に更新する */
  musicCrowdLowShelf: BiquadFilterNode;
  /** 人数比例の低音減衰 — voice bus 側。setCrowdCount で music 側と一緒に更新する */
  voiceCrowdLowShelf: BiquadFilterNode;
  /** Convolver 本体 (デバッグ用に露出) */
  convolver: ConvolverNode;
  /** 音楽バス専用のリミッター (music sum を -6dBFS で踏む、destination 直前)。 */
  musicLimiter: DynamicsCompressorNode;
  /** 声バス専用のリミッター (voice sum を -6dBFS で踏む、destination 直前)。music 側
   *  peak にダッキングされないよう musicLimiter とは独立。 */
  voiceLimiter: DynamicsCompressorNode;
  /** musicBus 直後 (= zone filter / limiter 通過前) の周波数分析用 AnalyserNode。
   *  scene.ts の reactive lighting (= 低/中/高域 + master energy で照明制御) が読む。
   *  voice / SE が混ざらない「DJ 配信そのもの」のスペクトラムを取る目的で、
   *  あえて musicBus に直接 tap する (zoneFilter / zoneGain 越しではない = プレイヤー
   *  位置に依らず DJ の演奏内容で照明が反応する)。
   *  destination には繋がない (= 計測専用、CPU は getByteFrequencyData 呼び出し時のみ)。 */
  musicAnalyser: AnalyserNode;

  // ─── Club Acoustic Simulator (= 「クラブの PA + ルームで聞いてる感」を作る) ─────
  // DJ 側で isolator は既に使用済 (= ストリームに焼き込まれてる)。listener 側は
  // ストリームをそのまま再生するが、「直挿しイヤホン」と「クラブで聞いた」のギャップを
  // 埋めるための emulation を 4 系統 parallel send で噛ます (= 既存 dry path と並列加算)。
  // Mancuso / Long のクラブ設計を DSP に翻訳した内容:
  //   - paSat: 軽い tanh で「PA アンプが鳴ってる」warmth (= 直挿しの「冷たさ」を消す)
  //   - subPresence: LP60 + tanh で missing-fundamental 体感低音、**sub に近づくほど強く**
  //   - hfReflection: HP5k + 40ms delay + feedback で天井/壁反射 shimmer、**壁に近いほど強く**
  //   - cornerBass: 80Hz lowshelf を **隅に近いほど boost** (= 室モード低音溜まり)
  // mobile では graph に乗せない (= null)。desktop でも clubAcousticEnabled で master gate。
  /** Club Acoustic 全 send を master gate するノード。OFF で 0、ON で 1.0。 */
  clubAcousticEnabled: GainNode | null;
  /** PA アンプ的 saturation の blend 量 (= 固定 0.2、LF only、position 非依存)。 */
  paSatBlend: GainNode | null;
  /** 体感低音 (sub-synth) の blend 量。live.ts が sub までの距離で modulate する。 */
  subPresenceBlend: GainNode | null;
  /** HF 反射 (multi-tap delay) の blend 量。live.ts が壁までの距離で modulate する。 */
  hfReflectionBlend: GainNode | null;
  /** 室モード低音 boost の lowshelf (60Hz)。live.ts が隅までの距離で gain を modulate する。 */
  cornerBassShelf: BiquadFilterNode | null;
  /** Club Acoustic ON/OFF を物理的に切り替える (= ramp + disconnect で CPU 完全カット)。
   *  ramp 中の click ノイズ対策 + connect/disconnect 順序を内部で管理。
   *  mobile では null (= simulator node 自体存在しない)。 */
  setClubAcousticEnabled: ((on: boolean) => void) | null;
  /** モバイル端末判定。UI 表示制御 + live.ts の position update スキップ判定に使う。 */
  isMobile: boolean;
}

/** Club Acoustic Simulator は WaveShaper + DelayNode + 複数 BiquadFilter で重いので、
 *  古い iPhone / Android では graph に乗せない (= AudioContext に追加ノードが乗らない)。
 *  /club は既に spatial audio + voice mesh で重いので、mobile では常時 OFF + UI も
 *  隠す方針。`navigator.maxTouchPoints` で iPad-on-iPadOS (= UA が desktop class) を
 *  追加検出する。 */
function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) return true;
  // iPadOS 13+ は UA が "Macintosh" になるが maxTouchPoints > 1。Mac desktop は 0。
  // 古い WebView で maxTouchPoints が undefined になる経路を null-safe にする
  // (Browser API review medium 対応)。
  const touchPoints = typeof navigator.maxTouchPoints === "number"
    ? navigator.maxTouchPoints
    : 0;
  if (touchPoints > 1 && /Macintosh/i.test(navigator.userAgent)) return true;
  return false;
}

export function createGraph(audioCtx: AudioContext, ambience: Ambience): ClubGraph {
  // 音楽バス
  const musicBus = audioCtx.createGain();
  musicBus.gain.value = 1.0;

  // 声バス
  const voiceBus = audioCtx.createGain();
  voiceBus.gain.value = 1.0;

  // Convolver と wet 系。
  // wetSend は scene が `setActiveZone(zone)` を呼んだ瞬間に zone.audio.reverbWet
  // へ補間されるが、scene 起動より先に live が musicBus に音を流し始めると
  // 「最初の数フレームだけ wet 0 (= ドライ)」 になる。ambience.defaultReverbWet
  // を初期値に置いておけば、scene tick まで「最低限の空気感」が掛かる。
  // convolver.buffer が IR ロード完了するまで実音は出ないので、defaultReverbWet が
  // 立っていても無音→reverb の急 attack にはならない。
  const wetSend = audioCtx.createGain();
  wetSend.gain.value = ambience.defaultReverbWet ?? 0;
  const masterWet = audioCtx.createGain();
  masterWet.gain.value = 1.0;
  const convolver = audioCtx.createConvolver();

  // 音楽専用のゾーンフィルタ
  const zoneFilter = audioCtx.createBiquadFilter();
  zoneFilter.type = "lowpass";
  zoneFilter.frequency.value = 18000;
  zoneFilter.Q.value = 0.7071;

  const zoneGain = audioCtx.createGain();
  zoneGain.gain.value = 1.0;

  // 群衆減衰 (bus ごとに 1 つずつ。setCrowdCount で両方同じ値を書き込む)
  const musicCrowdLowShelf = createCrowdLowShelf(audioCtx);
  const voiceCrowdLowShelf = createCrowdLowShelf(audioCtx);

  // music / voice それぞれ独立リミッター。両方とも -6dBFS / ratio 20 の固定パラメータ。
  // **destination 直前**に置くのが原則 (= "ここから先は何も増えない" を保証)。
  const musicLimiter = createLimiter(audioCtx);
  const voiceLimiter = createLimiter(audioCtx);

  // reactive lighting 用の周波数分析。fftSize=512 → frequencyBinCount=256、
  // sample rate 48kHz と仮定すると bin あたり ~94 Hz。smoothing=0.3 で 1 フレーム内の
  // FFT 揺れを軽くだけ均す (= envelope は scene 側で追加で持つ)。
  //
  // **入力接続は live.ts の attachAudioTrack 内で行う** (= musicBus ではなく
  // sourceUpmix に直接 tap)。理由:
  //   - musicBus tap だと各 speaker の distGain / lowpass を通過した後の sum 信号を
  //     見ることになり、player 位置で band energy が変動する (= 遠 speaker からの
  //     high band が落ちる等)。
  //   - sourceUpmix tap なら「WHEP source upmix 直後」= 全 player が同じ DJ 音を
  //     見るので、lighting が部屋全体で同期する共有体験になる。
  // attachAudioTrack 側で upmix.connect(musicAnalyser) する。reattach 時は
  // 旧 upmix.disconnect() で自動的に analyser 入力もクリアされる (= 重複なし)。
  const musicAnalyser = audioCtx.createAnalyser();
  musicAnalyser.fftSize = 512;
  musicAnalyser.smoothingTimeConstant = 0.3;

  // wet 経路の前段に **24dB/oct のカスケード lowpass** (= 2nd-order biquad ×2 =
  // Linkwitz-Riley 風 4th-order) を 250Hz で挟む。**低音のみ reverb tail を残す**。
  // 旧設計 (biquad 1 段 / 12dB/oct @ 300Hz) は 1kHz で -21dB しか落とせず、
  // 「中域の reverb がボワッと残る」体感を生んでいた (= 信号処理レビュー指摘)。
  // 4th-order @ 250Hz なら 1kHz で約 -48dB ≒ 実質ゼロ、500Hz で -24dB。
  // 250Hz 自体は -6dB (2 段で各 -3dB)、150Hz は -1dB 以下 = sub-bass はほぼ無傷。
  // 「kick / bass の sustain だけ部屋に残って中高域は dry のまま」感が真に作れる。
  const wetLowpass1 = audioCtx.createBiquadFilter();
  wetLowpass1.type = "lowpass";
  wetLowpass1.frequency.value = 250;
  wetLowpass1.Q.value = 0.7071;
  const wetLowpass2 = audioCtx.createBiquadFilter();
  wetLowpass2.type = "lowpass";
  wetLowpass2.frequency.value = 250;
  wetLowpass2.Q.value = 0.7071;

  // Club Acoustic Simulator (= 「クラブの PA + ルームで聞いてる感」)。
  // mobile では graph に乗せない (= 重い WaveShaper / DelayNode が AudioContext に乗らない)。
  // desktop でも setClubAcousticEnabled で master gate (= ramp + disconnect で CPU 完全カット)。
  // 4 系統:
  //   paSat:        zoneGain → LP400 → tanh(0.7x) → paSatBlend (= 固定 0.2、LF only) → ceSum
  //   subPresence:  zoneGain → LP60 → tanh(1.5x) → subPresenceBlend → ceSum
  //   hfReflection: zoneGain → HP5k → multi-tap delay (12/22/35/50ms) → hfReflectionBlend → ceSum
  //   cornerBass:   zoneGain → cornerBassShelf (lowshelf 60Hz) → ceSum
  // ceSum → clubAcousticEnabled (master gate, 0 or 1) → musicCrowdLowShelf
  // 既存 dry path (zoneGain → musicCrowdLowShelf) はそのまま並列で残り、enabled=0 で dry のみ。
  const isMobile = isMobileDevice();
  let clubAcousticEnabled: GainNode | null = null;
  let paSatBlend: GainNode | null = null;
  let subPresenceBlend: GainNode | null = null;
  let hfReflectionBlend: GainNode | null = null;
  let cornerBassShelf: BiquadFilterNode | null = null;
  let setClubAcousticEnabled: ((on: boolean) => void) | null = null;
  if (!isMobile) {
    const ceSum = audioCtx.createGain();
    ceSum.gain.value = 1.0;

    // (1) PA character saturation: **LP400 → tanh** で LF 帯域のみ saturate。
    //     full-bandwidth tanh だと中域に余計な harmonics が乗って vocal が smear。
    //     UREI 1620 と同じく「iron transformer 的 LF saturation」に統一。
    //     blend 0.2 (= LF only にしたので強めにしても中域は綺麗、Audio DSP review medium 対応)。
    const paSatLP = audioCtx.createBiquadFilter();
    paSatLP.type = "lowpass";
    paSatLP.frequency.value = 400;
    paSatLP.Q.value = 0.7071;
    const paSatShaper = audioCtx.createWaveShaper();
    paSatShaper.curve = makeTanhCurve(0.7, 4096);
    paSatShaper.oversample = "2x";
    paSatBlend = audioCtx.createGain();
    paSatBlend.gain.value = 0.2;
    zoneGain.connect(paSatLP);
    paSatLP.connect(paSatShaper);
    paSatShaper.connect(paSatBlend);
    paSatBlend.connect(ceSum);

    // (2) Sub presence: LP60 → tanh で missing-fundamental harmonics 生成。
    //     blend 量は live.ts が sub までの距離で 0..0.25 を modulate (= 近いほど強い)。
    const subLP = audioCtx.createBiquadFilter();
    subLP.type = "lowpass";
    subLP.frequency.value = 60;
    subLP.Q.value = 0.7071;
    const subShaper = audioCtx.createWaveShaper();
    subShaper.curve = makeTanhCurve(1.5, 4096);
    subShaper.oversample = "4x";
    subPresenceBlend = audioCtx.createGain();
    subPresenceBlend.gain.value = 0;
    zoneGain.connect(subLP);
    subLP.connect(subShaper);
    subShaper.connect(subPresenceBlend);
    subPresenceBlend.connect(ceSum);

    // (3) HF reflection: HP5k → **multi-tap delay** で diffuse な天井/壁反射 shimmer。
    //     旧設計 (= 単一 40ms delay + feedback 0.25) は slapback echo / コム filter
    //     resonance っぽく聞こえた。multi-tap (= 12/22/35/50ms に 0.4/0.3/0.2/0.15
    //     減衰) で時間的拡散を作り、自己 feedback は廃止 (Audio DSP review medium 対応)。
    //     blend 量は live.ts が壁までの距離で 0..0.15 を modulate (= 壁に近いほど強い)。
    const hfHP = audioCtx.createBiquadFilter();
    hfHP.type = "highpass";
    hfHP.frequency.value = 5000;
    hfHP.Q.value = 0.7071;
    hfReflectionBlend = audioCtx.createGain();
    hfReflectionBlend.gain.value = 0;
    zoneGain.connect(hfHP);
    const hfTaps: Array<[number, number]> = [
      [0.012, 0.4],
      [0.022, 0.3],
      [0.035, 0.2],
      [0.050, 0.15],
    ];
    for (const [delayTime, gain] of hfTaps) {
      const d = audioCtx.createDelay(0.1);
      d.delayTime.value = delayTime;
      const g = audioCtx.createGain();
      g.gain.value = gain;
      hfHP.connect(d);
      d.connect(g);
      g.connect(hfReflectionBlend);
    }
    hfReflectionBlend.connect(ceSum);

    // (4) Corner bass mode: lowshelf **60Hz** (旧 80Hz、Audio DSP review low 対応)。
    //     実機 room mode は部屋寸法で決まる 3rd モード前後の 50-60Hz が「角に溜まる」
    //     体感の核。80Hz はそこから外れて room mode の影響薄い帯域。
    //     gain は live.ts が隅までの距離で 0..+5dB を modulate。
    cornerBassShelf = audioCtx.createBiquadFilter();
    cornerBassShelf.type = "lowshelf";
    cornerBassShelf.frequency.value = 60;
    cornerBassShelf.gain.value = 0;
    zoneGain.connect(cornerBassShelf);
    cornerBassShelf.connect(ceSum);

    // master gate: gain.value = 0 で silence、ceSum を connect/disconnect で
    // 上流 DSP の CPU を完全に止める (SRE + Browser API review medium 対応)。
    clubAcousticEnabled = audioCtx.createGain();
    clubAcousticEnabled.gain.value = 0; // default は setClubAcousticEnabled で決定
    ceSum.connect(clubAcousticEnabled);
    clubAcousticEnabled.connect(musicCrowdLowShelf);

    let acousticConnected = true;
    const gateNode = clubAcousticEnabled;
    setClubAcousticEnabled = (on: boolean): void => {
      const t = audioCtx.currentTime;
      const tc = 0.05;
      if (on) {
        if (!acousticConnected) {
          ceSum.connect(gateNode);
          acousticConnected = true;
        }
        gateNode.gain.setTargetAtTime(1, t, tc);
      } else {
        gateNode.gain.setTargetAtTime(0, t, tc);
        if (acousticConnected) {
          // ramp 3τ = ~150ms 待ってから disconnect (= 上流 WaveShaper / DelayNode の
          // 計算自体を止めて CPU を完全に解放)
          setTimeout(() => {
            try { ceSum.disconnect(gateNode); } catch { /* already disconnected */ }
            acousticConnected = false;
          }, 200);
        }
      }
    };
  }

  // 配線:
  //   musicBus → zoneFilter → zoneGain ─┬→ musicCrowdLowShelf → musicLimiter → destination
  //                                     │           (dry, post-fader)
  //                                     │
  //                                     ├→ wetSend → wetLowpass1 → wetLowpass2 → convolver
  //                                     │                                          → masterWet
  //                                     │                                          → musicCrowdLowShelf
  //                                     │
  //                                     │   (desktop only) Club Acoustic Simulator:
  //                                     ├→ paSat (tanh) → paSatBlend (固定 0.1) ──┐
  //                                     ├→ subLP → subShaper → subPresenceBlend ──┤
  //                                     ├→ hfHP → hfDelay+fb → hfReflectionBlend ─┤
  //                                     └→ cornerBassShelf ───────────────────────┤
  //                                                                                ▼
  //                                                                              ceSum
  //                                                                                ↓
  //                                                                       clubAcousticEnabled (0/1)
  //                                                                                ↓
  //                                                                       musicCrowdLowShelf
  //
  //   voiceBus → voiceCrowdLowShelf → voiceLimiter → destination
  //
  // wetSend は zoneGain の **後ろ** から分岐させて post-fader send にする。
  // pre-fader 構成 (= musicBus 直接から wetSend) だと dry を絞った時に wet だけ残って
  // 「BAR で reverb が前に出てくる」逆転現象が起きる。
  // 24dB/oct カスケード LP で「kick / bass の sustain だけがクラブの空気感として残る」
  // 体感を作る (= 1kHz 以上の reverb tail は -48dB で実質ゼロ)。
  musicBus.connect(zoneFilter);
  zoneFilter.connect(zoneGain);
  zoneGain.connect(musicCrowdLowShelf);

  zoneGain.connect(wetSend);
  wetSend.connect(wetLowpass1);
  wetLowpass1.connect(wetLowpass2);
  wetLowpass2.connect(convolver);
  convolver.connect(masterWet);
  masterWet.connect(musicCrowdLowShelf);

  musicCrowdLowShelf.connect(musicLimiter);
  musicLimiter.connect(audioCtx.destination);

  voiceBus.connect(voiceCrowdLowShelf);
  voiceCrowdLowShelf.connect(voiceLimiter);
  voiceLimiter.connect(audioCtx.destination);

  // **Convolver IR を programmatic に生成** (= irUrl が指定されていないデフォルトでも
  // reverb が機能する)。noise burst + exponential decay の合成 IR で「クラブの
  // 中規模空間の響き」を低コストでシミュレート。1.8 秒の tail で「低音 sustain」
  // 感を出す。irUrl が明示されている場合は外部 IR を優先 (= 上書き)。
  convolver.buffer = makeClubIR(audioCtx, 1.8);
  if (ambience.irUrl) {
    void loadIR(audioCtx, ambience.irUrl)
      .then((buffer) => {
        convolver.buffer = buffer;
      })
      .catch((err) => {
        console.warn("[club] external IR load failed, fallback synthetic IR used", err);
      });
  }

  return {
    musicBus,
    voiceBus,
    wetSend,
    masterWet,
    zoneFilter,
    zoneGain,
    musicCrowdLowShelf,
    voiceCrowdLowShelf,
    convolver,
    musicLimiter,
    voiceLimiter,
    musicAnalyser,
    clubAcousticEnabled,
    paSatBlend,
    subPresenceBlend,
    hfReflectionBlend,
    cornerBassShelf,
    setClubAcousticEnabled,
    isMobile,
  };
}

/** tanh(x * drive) 形の soft saturation curve を sample 数分生成する。
 *  -1..+1 の x に対して tanh で柔らかい飽和をかけ、サブベース合成 path の
 *  「missing fundamental」感を作る。drive=1.5 で軽い倍音生成。
 *  WaveShaper.curve の setter は Float32Array<ArrayBuffer> を要求するので、
 *  ArrayBuffer を明示的に作って backing にする (= SharedArrayBuffer 排除)。 */
function makeTanhCurve(drive: number, samples: number): Float32Array<ArrayBuffer> {
  const buf = new ArrayBuffer(samples * 4);
  const curve = new Float32Array(buf);
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * drive);
  }
  return curve;
}

function createLimiter(audioCtx: AudioContext): DynamicsCompressorNode {
  const limiter = audioCtx.createDynamicsCompressor();
  limiter.threshold.value = LIMITER_THRESHOLD_DB;
  limiter.knee.value = LIMITER_KNEE;
  limiter.ratio.value = LIMITER_RATIO;
  limiter.attack.value = LIMITER_ATTACK;
  limiter.release.value = LIMITER_RELEASE;
  return limiter;
}

function createCrowdLowShelf(audioCtx: AudioContext): BiquadFilterNode {
  const lowShelf = audioCtx.createBiquadFilter();
  lowShelf.type = "lowshelf";
  lowShelf.frequency.value = 80;
  lowShelf.gain.value = 0;
  return lowShelf;
}

async function loadIR(audioCtx: AudioContext, url: string): Promise<AudioBuffer> {
  const res = await fetch(url, { cache: "force-cache" });
  if (!res.ok) throw new Error(`IR fetch ${res.status}`);
  const arr = await res.arrayBuffer();
  return await audioCtx.decodeAudioData(arr);
}

/** programmatic に「中規模クラブの reverb IR」を生成する。
 *  noise burst + exponential decay の合成で、tail は durationSec で指定。
 *  低音帯のみが convolver に入る (= wetLowpass で band 制限) 前提なので、
 *  IR 自体は white noise のフラットな decay で十分。低音 sustain の体感は
 *  wetLowpass + IR tail の合わせ技で生じる。
 *  ステレオ 2ch で独立な乱数を生成 → 自然な左右広がり。 */
function makeClubIR(audioCtx: AudioContext, durationSec = 1.5): AudioBuffer {
  const sr = audioCtx.sampleRate;
  const length = Math.floor(sr * durationSec);
  const ir = audioCtx.createBuffer(2, length, sr);
  // decay の急峻度。3.0 で「ライブハウス感」、5.0 で「タイトな部屋」
  const decayPow = 3.0;
  for (let ch = 0; ch < ir.numberOfChannels; ch++) {
    const data = ir.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      const t = i / length;
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, decayPow);
    }
  }
  return ir;
}
